import { put, select, takeEvery, debug, LOGOUT } from "../../lib";
import {
  AUTH_REFRESH_UNAVAILABLE,
  AUTH_REFRESHING_FAILURE,
  REQUEST_COMPLETE,
  REQUEST_ERROR,
  REQUEST_SKIP,
  doCONNECTIVITY_SERVICE_UNAVAILABLE_SET,
  doCONNECTIVITY_FAILED_DURING_OUTAGE_SET,
} from "../state";
import { REQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT } from "..";

// the outcomes an ordinary request reports when the backend is unreachable rather than
// unhappy. `null` is what `baseRequest` reports for a transport failure (no response at all);
// `undefined` is NOT in this list on purpose -- a REQUEST_ERROR raised by schema validation never
// reached the network and carries no `statusCode`, and an outage notice would be a lie.
export const SERVICE_UNAVAILABLE_STATUS_LIST: ReadonlyArray<number | null> = [null, 502, 503, 504];

// Checked inside a function, NOT captured at module scope: this module and the '..' barrel are
// part of the existing lib/store init cycle, so the constant is still `undefined` while this file
// is being evaluated. A module-scope capture would compare against `undefined` -- silently, with
// no throw -- and every refresh failure would raise the ordinary-503 notice on top of the
// reconnecting one. See the same note in `handle_expired_token.ts`.
const isRefreshRequest = (fromType: string): boolean =>
  fromType === REQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT;

// the same notion of a request action as `handle_loading_state.ts`
const isRequestAction = (type: string): boolean => /^REQUEST|SUBMIT$/.test(type);

// Every action the exposure tracker reads except REQUEST_ERROR, which `handleServiceUnavailable`
// reads and retires itself so the check and the cleanup cannot run in the wrong order. Built per
// call for the init-cycle reason above.
const isTrackedAction = ({ type }: { type: string }): boolean =>
  type !== REQUEST_ERROR &&
  (isRequestAction(type) ||
    [AUTH_REFRESH_UNAVAILABLE, AUTH_REFRESHING_FAILURE, LOGOUT].includes(type));

/**
 * The requests that were unanswered at some point while the refresh coordinator was reconnecting.
 * Sampling `auth.refreshUnavailable` when an error lands is not enough: a request sent during the
 * outage can fail only after the refresh has succeeded -- ordinary requests set no timeout, so a hung
 * one fails whenever the connection gives up. Kept per running saga, so one store's outage never
 * reaches another's.
 */
interface OutageExposure {
  inFlight: Set<string>;
  exposed: Set<string>;
}

function* trackOutageExposure(exposure: OutageExposure, { type, payload }: any) {
  if (type === AUTH_REFRESH_UNAVAILABLE) {
    exposure.inFlight.forEach((requestId) => exposure.exposed.add(requestId));
    return;
  }

  // The session ended instead of recovering, so a late failure from it has no page to reload. Its
  // pending requests go too: left in `inFlight`, the next session's outage would adopt them.
  if (type === AUTH_REFRESHING_FAILURE || type === LOGOUT) {
    exposure.inFlight.clear();
    exposure.exposed.clear();
    return;
  }

  const requestId = payload?.requestId;
  if (typeof requestId !== "string") return;

  // `takeLeading` drops every refresh dispatch that lands on a running cycle, and a dropped one never
  // completes, so tracking it would only leave ids behind. Its failures are not counted anyway.
  if (isRefreshRequest(type)) return;

  if (type === REQUEST_COMPLETE || type === REQUEST_SKIP) {
    exposure.inFlight.delete(requestId);
    exposure.exposed.delete(requestId);
    return;
  }

  exposure.inFlight.add(requestId);
  const reconnecting: boolean = yield select((state: any) =>
    Boolean(state?.auth?.refreshUnavailable),
  );
  if (reconnecting) exposure.exposed.add(requestId);
}

/**
 * Ordinary-503 detection. A non-refresh request that answered 502/503/504, or did not
 * answer at all, stamps `connectivity.serviceUnavailableAt`; `ConnectionNoticeBanner` shows the
 * temporary-unavailable notice for a few seconds and nothing is retried.
 *
 * When the failed request was unanswered at some point during a reconnecting outage, whether it fails
 * during the outage or after it, the same failure also sets `connectivity.failedDuringOutage`, so that
 * once the refresh succeeds the banner can say the page may be out of date and offer a reload. Nothing
 * re-runs the request itself: a failed mutation must never be re-sent behind a UI that has already
 * shown its failure, and a reload only re-runs the page's fetches.
 *
 * The transient refresh attempts dispatch no REQUEST_ERROR at all, so they never reach here --
 * this guard only covers the refresh's terminal failures, whose story the reconnecting notice and
 * the login page already tell.
 */
export function* handleServiceUnavailable(exposure: OutageExposure, { payload }: any) {
  debug("saga*handleServiceUnavailable");

  const { fromType, statusCode, requestId } = payload ?? {};

  const caughtByOutage = exposure.exposed.has(requestId);
  exposure.inFlight.delete(requestId);
  exposure.exposed.delete(requestId);

  if (!SERVICE_UNAVAILABLE_STATUS_LIST.includes(statusCode)) return;
  if (isRefreshRequest(fromType)) return;

  yield put(doCONNECTIVITY_SERVICE_UNAVAILABLE_SET({ serviceUnavailableAt: Date.now() }));

  const reconnecting: boolean = yield select((state: any) =>
    Boolean(state?.auth?.refreshUnavailable),
  );
  if (reconnecting || caughtByOutage)
    yield put(doCONNECTIVITY_FAILED_DURING_OUTAGE_SET({ failedDuringOutage: true }));
}

export function* handleServiceUnavailableSaga() {
  const exposure: OutageExposure = { inFlight: new Set(), exposed: new Set() };
  yield takeEvery(isTrackedAction, trackOutageExposure, exposure);
  yield takeEvery(REQUEST_ERROR, handleServiceUnavailable, exposure);
}
